<template>
  <div>
    <!-- Page Header Start -->
    <div class="page-title-header">
      <BreadCrumb
        :parentRouteName="breadcrumbParentRoute"
        :extra-items="breadcrumbExtraItems"
      />
    </div>
    <!-- Page Header End -->

    <!-- Form Start -->
    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-12">
          <b-overlay :show="showLoading">
            <b-card>
              <!-- begin: dynamic form -->
              <div class="preserve-spacing-for-datetime-picker">
                <h4 class="mb-2 font-weight-bold">
                  {{ pageTitle }}
                </h4>

                <b-form-group
                  v-if="isOneTimeBooking"
                  label="預約號碼"
                  label-cols-sm="3"
                  label-cols-lg="2"
                  content-cols-lg="9"
                >
                  <label class="col-form-label" style="font-size: 1rem">{{ booking.booking_number }}</label>
                </b-form-group>

                <b-form-group
                  v-bind:label="'預約會員'"
                  label-cols-sm="3"
                  label-cols-lg="2"
                  content-cols-lg="9"
                  style="width: 100%"
                >
                  <div
                    v-if="selectedCustomer"
                    class="d-flex align-items-center py-2 staff-info"
                  >
                    <b-avatar
                      class="mx-2"
                      :src="selectedCustomer.avatar_url"
                    ></b-avatar>
                    <div
                      class="mr-auto font-weight-bold text-nowrap text-truncate"
                    >
                      {{ selectedCustomer.name }}
                      <span class="staff-list-info">
                        {{ selectedCustomer.email }}<br />
                        {{ displayCustomerInfo(selectedCustomer) }}
                      </span>
                    </div>
                  </div>
                  <StaffAddCustomer
                    v-if="!isReadOnly"
                    ref="staffAddCustomer"
                    :add-button-text="addCustomerButtonText"
                    @bind="handleSelectCustomer"
                    :sourceType="'line'"
                  ></StaffAddCustomer>
                </b-form-group>

                <!-- wizard 模式 start-->
                <div class="sorted-fields">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="分店"
                    label-for="staff"
                    v-if="dataModel.isWizardMode && dataModel.branchSelectionIsEnabled"
                    :style="{ order: fieldOrder.branch }"
                  >
                    <AppSingleSelect
                      class="mr-3"
                      v-model="booking.select_branch"
                      :options="branchOptions"
                      @input="handleBranchChange"
                      :disabled="isReadOnly || disabledBranchSelect"
                    />
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="員工"
                    label-for="staff"
                    v-if="dataModel.isWizardMode && dataModel.staffSelectionIsEnabled"
                    :style="{ order: fieldOrder.staff }"
                  >
                    <div class="d-flex">
                      <b-form-text
                        v-if="booking.staff"
                        class="mb-2 mr-sm-2 mb-sm-0"
                      >
                        {{ booking.staff.name }}
                      </b-form-text>
                      <b-button
                        v-if="!isReadOnly"
                        class="mb-xl-0"
                        variant="primary"
                        @click="$refs.staffSelection.show()"
                        :disabled="!booking.branch_id"
                      >
                        <b-spinner v-if="isFetchingStaffs" small></b-spinner>
                        <i class="fa fa-plus" v-else></i>
                        選擇員工
                      </b-button>
                    </div>
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="服務"
                    label-for="service"
                    v-if="dataModel.isWizardMode && dataModel.serviceFieldId"
                    :style="{ order: fieldOrder.service }"
                  >
                    <b-overlay :show="isLoadingServices">
                      <div v-if="canMultipleSelect">
                        <b-form-checkbox-group
                          v-model="booking.service_id"
                          :options="serviceOptions"
                          :disabled="isReadOnly"
                          @input="handleServiceSelected"
                        >
                        </b-form-checkbox-group>
                      </div>
                      <div v-else>
                        <b-form-radio-group
                          v-model="booking.service_id"
                          :options="serviceOptions"
                          :disabled="isReadOnly"
                          @input="handleServiceSelected"
                        >
                        </b-form-radio-group>
                      </div>
                    </b-overlay>
                  </b-form-group>

                  <b-form-group
                    v-if="dataModel.isWizardMode && dataModel?.serviceFieldId"
                    label="服務時長"
                    label-cols="2"
                    :style="{ order: dataModel?.wizardFieldOrder?.service }"
                  >
                    <div class="d-flex align-items-center">
                      <span class="mr-2">{{ serviceDuration }} 分鐘</span>
                    </div>
                  </b-form-group>
                </div>
                <!-- wizard 模式 end-->

                <DynamicFormDashboard
                  ref="dynamicForm"
                  :input-subjects="visibleSubjects"
                  v-model="inputForm"
                ></DynamicFormDashboard>

                <!-- 補充時間欄位 start-->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="開始時間"
                  label-for="start_at"
                  v-if="false === hasSetUpFieldMappingForStartAt"
                >
                  <AppDateTimePicker
                    id="start_at"
                    v-model="start_at"
                    :hasError="deepGet(v$, 'start_at.$error')"
                    :disabled="isReadOnly"
                  ></AppDateTimePicker>
                  <b-form-invalid-feedback
                    :state="!deepGet(v$, 'start_at.$error')"
                  >
                    輸入的資料有誤或未填寫，請確認
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="結束時間"
                  label-for="end_at"
                  v-if="shouldShowDefaultEndAtField"
                >
                  <AppDateTimePicker
                    id="end_at"
                    v-model="end_at"
                    :hasError="deepGet(v$, 'end_at.$error')"
                    :disabled="isReadOnly"
                  ></AppDateTimePicker>
                  <b-form-invalid-feedback
                    :state="!deepGet(v$, 'end_at.$error')"
                  >
                    輸入的資料有誤或未填寫，請確認
                  </b-form-invalid-feedback>
                </b-form-group>
                <!-- 補充時間欄位 end-->

                <!-- 週期性設定 Start -->
                <template v-if="isPeriodicBooking">
                  <hr v-if="isEditing" class="my-4">
                  <h5 v-if="isEditing" class="mb-3">修改範圍</h5>

                  <b-form-group v-if="isEditing">
                    <b-form-radio-group v-model="scope">
                      <label class="mutation-scope">
                        <b-form-radio value="this">
                          <div>
                            <strong>這次預約</strong>
                            <p class="mutation-scope__note">只修改選定日期的預約</p>
                          </div>
                        </b-form-radio>
                      </label>
                      <label class="mutation-scope">
                        <b-form-radio value="future">
                          <div>
                            <strong>這次預約與後續預約</strong>
                            <p class="mutation-scope__note">修改選定日期及之後的所有預約</p>
                          </div>
                        </b-form-radio>
                      </label>
                      <label class="mutation-scope" :class="{ disabled: startAtDateIsChanged }">
                        <b-form-radio value="all" :disabled="startAtDateIsChanged">
                          <div>
                            <strong>所有預約</strong>
                            <p class="mutation-scope__note text-danger" v-if="startAtDateIsChanged">開始日期已變更，無法套用至所有預約</p>
                            <p class="mutation-scope__note" v-else>修改此週期性預約所有尚未開始的預約記錄</p>
                          </div>
                        </b-form-radio>
                      </label>
                    </b-form-radio-group>
                  </b-form-group>

                  <template v-if="(isEditing && scope !== 'this') || !isEditing">
                    <hr class="my-4">
                    <h5 class="mb-3">重複模式</h5>

                    <b-form-group label="重複頻率" label-cols="2">
                      <b-form-radio-group
                        v-model="periodicConfig.period_type"
                        @change="handleRecurrenceTypeChange"
                      >
                        <b-form-radio value="daily">每天</b-form-radio>
                        <b-form-radio value="weekly">每週</b-form-radio>
                        <b-form-radio value="monthly">每月</b-form-radio>
                      </b-form-radio-group>
                    </b-form-group>

                    <b-form-group label="間隔" label-cols="2">
                      <b-form-input
                        v-model.number="periodicConfig.period_interval"
                        type="number"
                        :placeholder="`每 N ${intervalUnit}`"
                      />
                    </b-form-group>

                    <b-form-group
                      v-if="periodicConfig.period_type === 'weekly'"
                      label="星期"
                      label-cols="2"
                    >
                      <b-form-select v-model="periodicConfig.period_day_of_week">
                        <option :value="0">星期日</option>
                        <option :value="1">星期一</option>
                        <option :value="2">星期二</option>
                        <option :value="3">星期三</option>
                        <option :value="4">星期四</option>
                        <option :value="5">星期五</option>
                        <option :value="6">星期六</option>
                      </b-form-select>
                    </b-form-group>

                    <b-form-group
                      v-if="periodicConfig.period_type === 'monthly'"
                      label="日期"
                      label-cols="2"
                    >
                      <b-form-input
                        v-model.number="periodicConfig.period_day_of_month"
                        type="number"
                        placeholder="1~31"
                      />
                    </b-form-group>

                    <b-form-group label="結束日期" label-cols="2">
                      <b-form-radio-group v-model="lastBookingDateMode">
                        <div class="d-inline-flex align-items-center">
                          <b-form-radio :value="null">持續不停</b-form-radio>
                          <div class="d-inline-flex align-items-center">
                            <b-form-radio value="specified" class="mr-0"></b-form-radio>
                            於
                            <AppDatePicker v-model="lastBookingDate" class="ml-2" />
                          </div>
                        </div>
                      </b-form-radio-group>
                    </b-form-group>
                  </template>

                  <hr class="my-4">
                  <h5 class="mb-3">預覽</h5>

                  <div v-if="previewDates.length" class="mt-3">
                    <h6>接下來的預約：</h6>
                    <ul class="list-unstyled">
                      <li v-for="(dateRange, index) in previewDates" :key="index" class="mb-1">
                        <i class="fa fa-calendar text-primary"></i>
                        <span v-if="dateRange.end">
                          {{ formatDate(dateRange.start) }} ~ {{ formatDate(dateRange.end) }}
                        </span>
                        <span v-else>
                          {{ formatDate(dateRange.start) }}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div v-else class="text-muted">
                    <p>請填寫開始時間和週期設定以預覽預約</p>
                  </div>
                </template>
                <!-- 週期性設定 End -->

                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center mt-4">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button
                    v-if="!isReadOnly"
                    class="mr-3"
                    @click="submit"
                    variant="success"
                  >
                    儲存
                  </b-button>
                </div>
                <!-- 表單底部 End -->
              </div>
              <!-- end: dynamic form -->
            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
    <!-- Form End -->
    <StaffSelectionModal
      ref="staffSelection"
      :staffs="availableStaffs"
      :is-loading="isFetchingStaffs"
      @selected="handleStaffSelected"
    ></StaffSelectionModal>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import bookingApi from "@/apis/booking";
import bookingPresetApi from '@/apis/booking-preset'
import AppDateTimePicker from "@/components/AppDateTimePicker";
import DynamicFormDashboard from "@/components/DynamicForm/DynamicFormDashboard";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import StaffAddCustomer from "@/pages/Dashboard/Staff/StaffAddCustomer.vue";
import _ from "lodash";
import moment from "moment";
import Vue from "vue";
import { mapGetters } from "vuex";
import StaffSelectionModal from "@/components/Dashboard/Staff/StaffSelectionModal.vue";
import collectionApi from "@/apis/collection";
import AppDatePicker from '@/components/AppDatePicker.vue';
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BookingPresetDataModel from "@/models/BookingPreset.model";
import DynamicFormField from "@/models/DynamicFormField.model"
import DynamicFormValuePresenter from "@/presenters/DynamicFormValue.presenter"
import BookingBusinessLogic from "@/utils/booking/BookingBusinessLogic";
import ServiceDataFetcher from "@/utils/booking/ServiceDataFetcher";
import StaffDataFetcher from "@/utils/booking/StaffDataFetcher"
import { previewDatesForCreating, previewDatesForExisting } from '@/utils/periodicBookingHelper';
import { passesVisibleCondition } from "@/utils/DynamicFormUtils"
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

const bookingBusinessLogic = new BookingBusinessLogic()

const serviceDataFetcher = Vue.observable(new ServiceDataFetcher())
serviceDataFetcher.injectCollectionApi(collectionApi)

const staffDataFetcher = Vue.observable(new StaffDataFetcher())
staffDataFetcher.injectBookingPresetApi(bookingPresetApi)

export default {
  components: {
    AppSingleSelect,
    AppDatePicker,
    AppDateTimePicker,
    DynamicFormDashboard,
    StaffAddCustomer,
    StaffSelectionModal,
    BreadCrumb
  },
  setup: () => ({ v$: useVuelidate({ $scope: false }) }),
  validations() {
    return {
      start_at: this.hasSetUpFieldMappingForStartAt ? {} : { required },
      end_at: this.shouldShowDefaultEndAtField ? { required } : {},
    };
  },
  data() {
    return {
      consts,
      showLoading: false,
      bookingPreset: {
        name: null,
        config: {
          fields: [],
        },
      },
      start_at: null,
      end_at: null,
      inputForm: {},
      booking: {
        customer_id: null,
        service_id: [],
        staff_id: null,
        branch_id: null,
        start_at: null,
        end_at: null,
        data: [],
        select_branch: {}
      },
      selectedCustomer: null,
      collectionOfService: null,
      oneTimeBooking: null,
      periodicBooking: null,
      periodicConfig: {
        period_type: 'daily',
        period_interval: 1,
        period_day_of_week: 0,
        period_day_of_month: 1,
      },
      lastBookingDate: null,
      scope: 'this',
      lastBookingDateMode: null,
      originalStartAt: null,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    ...mapGetters("general", ["myBranches", "currentBranch"]),
    organizationId() {
      return this.$store.state.general.organization.id;
    },
    targetDate() {
      return this.$route.params.date;
    },
    addCustomerButtonText() {
      return this.selectedCustomer ? "重新選擇會員" : "選擇此預約所屬會員";
    },
    bookingPresetIdFromRoute() {
      return this.$route.params.id
    },
    bookingIdFromRoute() {
      return this.$route.params.booking_id
    },
    periodicBookingIdFromRoute() {
      return this.$route.params.periodic_booking_id
    },
    isBookingCenter() {
      return this.$route.name.includes("BookingCenter");
    },
    isEditing() {
      return ["BookingEdit", "BookingCenterBookingEdit", 'PeriodicBookingEdit'].includes(this.$route.name)
    },
    isReadOnly() {
      return ["BookingView", "BookingCenterBookingView"].includes(this.$route.name)
    },
    isOneTimeBooking() {
      return !this.isPeriodicBooking
    },
    isPeriodicBooking() {
      return ['PeriodicBookingCreate', 'PeriodicBookingEdit'].includes(this.$route.name)
    },
    disabledBranchSelect() {
      return !this.checkPermission([this.consts.BOOKING_MANAGE_BRANCH]) &&
        (this.isEditing || this.booking.branch_id)
    },
    dataModel() {
      return new BookingPresetDataModel(this.bookingPreset)
    },
    hasSetUpFieldMappingForStartAt() {
      return this.dataModel.hasSetUpFieldMappingForStartAt
    },
    hasSetUpFieldMappingForEndAt() {
      return this.dataModel.hasSetUpFieldMappingForEndAt
    },
    canMultipleSelect() {
      return this.dataModel.canSelectMultipleServices
    },
    branchFieldId() {
      return this.dataModel.branchFieldId
    },
    branchField() {
      return this.bookingPreset.config.fields.find((field) => field._id === this.branchFieldId)
    },
    selectedBranchId() {
      return this.booking.select_branch?.value ?? null
    },
    selectedBranch() {
      return this.myBranches.find(branch => branch.id === this.selectedBranchId)
    },
    branchValueForDynamicForm() {
      return this.selectedBranchId ? DynamicFormValuePresenter.branch(this.branchField, this.selectedBranch) : null
    },
    serviceFieldId() {
      return this.dataModel.serviceFieldId
    },
    serviceField() {
      return this.bookingPreset.config.fields.find((field) => field._id === this.serviceFieldId)
    },
    collectionIdForService() {
      return this.dataModel.collectionIdForService
    },
    fieldOrder() {
      return this.dataModel.wizardFieldOrder
    },
    isLoadingServices() {
      return serviceDataFetcher.isFetching
    },
    serviceOptions() {
      return serviceDataFetcher.services.map(service => ({
        value: service.id,
        text: service.title,
      }))
    },
    branchOptions() {
      const options = [
        { value: null, text: "請選擇分店" },
        ...this.myBranches.map((branch) => ({ value: branch.id, text: `${branch.branch_code} ${branch.name}` })),
      ];

      if (!options.find((o) => o.value == this.booking.branch_id)) {
        options.push({ value: this.booking.branch_id, text: this.booking.branch.name });
      }
      return options;
    },
    dynamicFormFields() {
      return DynamicFormField.fromArray(_.get(this.bookingPreset, 'config.fields', [])).map((fieldModel) => {
        return {
          ...fieldModel.flattenedAttributes(),
          disabled: this.isEditing && fieldModel._id == this.branchFieldId && fieldModel.type == 'shop_selection',
        };
      });
    },
    visibleSubjects() {
      return this.dynamicFormFields.filter(subject => {
        return passesVisibleCondition(_.get(subject, "visible_when_any", []), this.dynamicFormFields, this.inputForm) &&
          !(this.dataModel.isWizardMode && [this.branchFieldId, this.serviceFieldId].includes(subject._id)) // 已在 DynamicForm 之上顯示分店與服務，因此 DynamicForm 不需再次顯示
      }).map(subject => ({
        ...subject,
        disabled: subject.disabled || this.isReadOnly,
      }));
    },
    isFetchingStaffs() {
      return staffDataFetcher.isFetching
    },
    availableStaffs() {
      return staffDataFetcher.staffs
    },
    selectedServiceIdsAsArray() {
      if (!this.booking.service_id) {
        return []
      }
      return Array.isArray(this.booking.service_id) ? this.booking.service_id : [this.booking.service_id]
    },
    selectedServices() {
      return serviceDataFetcher.services.filter(service => this.selectedServiceIdsAsArray.includes(service.id))
    },
    serviceDuration() {
      return _.sumBy(this.selectedServices, 'duration')
    },
    serviceValueForDynamicForm() {
      return this.selectedServices
        .map(service => DynamicFormValuePresenter.collectionItem(this.serviceField, service._rawData, this.collectionOfService))
    },
    startAtDateIsChanged() {
      if (!this.originalStartAt || !this.valueOfStartAt) return false;
      return moment(this.originalStartAt).format('YYYY-MM-DD') !== moment(this.valueOfStartAt).format('YYYY-MM-DD');
    },
    valueOfStartAt() {
      if (!this.hasSetUpFieldMappingForStartAt) {
        return this.start_at
      }
      return this.inputForm[this.dataModel.startAtFieldId]
    },
    valueOfEndAt() {
      if (this.dataModel.isWizardMode && !this.isEditing) {
        return bookingBusinessLogic.endDateTimeToSubmitInWizard(this.valueOfStartAt, this.serviceDuration).format('YYYY-MM-DD HH:mm:ss')
      }

      if (!this.hasSetUpFieldMappingForEndAt) {
        return this.end_at
      }
      return this.inputForm[this.dataModel.endAtFieldId]
    },
    shouldShowDefaultEndAtField() {
      return (this.dataModel.isWizardMode && this.isEditing) ||
        (!this.dataModel.isWizardMode && !this.hasSetUpFieldMappingForEndAt)
    },
    endAtFieldIsVisible() {
      if (this.dataModel.startAtFieldId === this.dataModel.endAtFieldId) {
        return false;
      }
      return this.visibleSubjects.some(subject => subject._id === this.dataModel.endAtFieldId);
    },
    previewDates() {
      const startValue = this.valueOfStartAt;
      const endValue = this.valueOfEndAt;
      const timing_config = this.periodicConfig;
      const last_booking_date = this.lastBookingDate;

      let dates = [];

      if (this.isEditing) {
        dates = previewDatesForExisting({
          timing_config,
          first_booking_date: this.periodicBooking?.first_booking_date,
          last_booking_date,
          start_at: startValue,
          end_at: endValue
        }, this.scope, 5);
      } else {
        dates = previewDatesForCreating({
          timing_config,
          last_booking_date,
          start_at: startValue,
          end_at: endValue
        }, 5);
      }

      if (!this.endAtFieldIsVisible) {
        // only one time field is visible or end time field is hidden, preview should only show the start time
        dates = dates.map(({ start, end }) => ({ start })); // eslint-disable-line no-unused-vars
      }

      return dates;
    },
    breadcrumbParentRoute() {
      if (this.isOneTimeBooking && this.isBookingCenter) {
        return ''
      }
      return 'BookingSelectList'
    },
    breadcrumbExtraItems() {
      if (this.isPeriodicBooking) {
        return [
          {
            text: this.bookingPreset?.name || '',
            to: 'BookingList',
            params: { id: this.bookingPresetIdFromRoute }
          },
          this.isEditing ? '編輯週期性預約' : '新增週期性預約'
        ]
      }

      return this.isBookingCenter ? [
        '登記預約中心',
        {
          text: this.bookingPreset.name ?? '',
          to: 'BookingCenterBookingList',
          params: this.bookingPresetIdFromRoute
        },
        this.isReadOnly ? '檢視預約' : this.isEditing ? '編輯預約' : '新增預約'
      ] : [
        {
          text: this.bookingPreset.name ?? '',
          to: 'BookingList',
          params: this.bookingPresetIdFromRoute
        },
        this.isReadOnly ? '檢視預約' : this.isEditing ? '編輯預約' : '新增預約'
      ]
    },
    pageTitle() {
      if (this.isPeriodicBooking) {
        return (this.isEditing ? '編輯週期性預約' : '新增週期性預約') + (this.bookingPreset ? ' - ' + this.bookingPreset.name : '')
      }
      return this.isReadOnly ? "檢視預約" : (this.isEditing ? "編輯預約" : "新增預約") + " - " + this.bookingPreset.name
    },
    intervalUnit() {
      const units = {
        daily: '天',
        weekly: '週',
        monthly: '月'
      };
      return units[this.periodicConfig.period_type] || '';
    },
  },
  watch: {
    startAtDateIsChanged(newVal) {
      if (newVal && this.isEditing && this.scope === 'all') {
        this.scope = 'this';
      }
    }
  },
  methods: {
    deepGet: _.get,
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async init() {
      const initializeDataForForm = async () => {
        if (this.isEditing || this.isReadOnly) {
          const loadOneTimeBookingData = async () => {
            if (this.bookingIdFromRoute) {
              await this.fetchOneTimeBooking();
            }
          }

          const loadPeriodicBookingData = async () => {
            if (this.periodicBookingIdFromRoute) {
              await this.fetchPeriodicBooking();
            }
          }

          await Promise.all([
            loadOneTimeBookingData(),
            loadPeriodicBookingData(),
          ])

          if (this.bookingIdFromRoute) {
            this.setBookingMainData(this.oneTimeBooking, this.oneTimeBooking.data)
          } else if (this.periodicBookingIdFromRoute) {
            this.setBookingMainData(this.periodicBooking, this.periodicBooking.field_values)
          }

          this.booking.select_branch = this.branchOptions.find(branch => branch.value === this.booking.branch_id);
        } else {
          this.booking.branch_id = this.$route.query.branch_id || this.currentBranch?.id
          if (this.booking.branch_id) {
            this.booking.select_branch = this.branchOptions.find(branch => branch.value === this.booking.branch_id)
            this.$set(this.inputForm, this.branchFieldId, this.branchValueForDynamicForm)
          }
        }

        await Promise.all([
          initializeServices(),
          initializeStaffs(),
        ])
      }

      const initializeServices = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        if (bookingBusinessLogic.servicesAreBoundToBranches() && this.booking.branch_id) {
          await serviceDataFetcher.fetchServicesByBranch(this.collectionIdForService, this.booking.branch_id)
        } else if (bookingBusinessLogic.servicesAreBoundToStaffs() && this.booking.staff_id) {
          await serviceDataFetcher.fetchServicesByStaff(this.collectionIdForService, this.booking.staff_id)
        } else {
          await serviceDataFetcher.fetchServices(this.collectionIdForService)
        }

        if (this.selectedServiceIdsAsArray.length > 0) {
          this.$set(this.inputForm, this.serviceFieldId, this.serviceValueForDynamicForm)
        }
      }

      const initializeStaffs = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        if (bookingBusinessLogic.staffsAreBoundToBranches() && this.booking.branch_id) {
          await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
        } else if (bookingBusinessLogic.staffsAreBoundToServices() && this.selectedServiceIdsAsArray.length > 0) {
          await staffDataFetcher.fetchAvailableStaffs(this.selectedServiceIdsAsArray, this.booking.branch_id, this.bookingPresetIdFromRoute)
        } else if (this.booking.branch_id) {
          await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
        }
      }

      const initializeCollectionOfService = async () => {
        if (!this.dataModel.isWizardMode) {
          return
        }

        this.collectionOfService = _.get(await collectionApi.getCollection(this.collectionIdForService), 'data.data')
      }

      const initializeDynamicForm = async () => {
        if (this.$refs.dynamicForm) {
          await this.$refs.dynamicForm.fetchAllAsyncOptions();
        }
      }

      this.showLoading = true;
      await this.fetchBookingPreset();
      await Promise.all([
        initializeCollectionOfService(),
        initializeDataForForm(),
        initializeDynamicForm(),
      ]);
      this.showLoading = false;
    },
    cancel() {
      this.$router.go(-1);
    },
    async submit() {
      // 先驗證 vuelidate 的字段
      const isVuelidateValid = await this.v$.$validate();

      // 如果 vuelidate 驗證不通過，退出函數並顯示錯誤
      if (!isVuelidateValid) {
        const element = document.getElementById(this.v$.$errors[0].$property);
        element.scrollIntoView({ behavior: "smooth" });
        console.warn("[BookingForm] Validation failed.");
        return;
      }

      const validate = await this.$refs.dynamicForm.validate();
      if (!validate) return;

      try {
        this.showLoading = true;

        const bookingDataToSave = {
          fields: DynamicFormField.fromArray(this.bookingPreset.config.fields).reduce((acc, fieldModel) => {
            return { ...acc, [fieldModel._id]: this.inputForm[fieldModel._id] ?? null };
          }, {}),
          customer_id: this.selectedCustomer?.id,
          start_at: this.valueOfStartAt,
          end_at: this.valueOfEndAt ? moment(this.valueOfEndAt).format('YYYY-MM-DD HH:mm:ss') : null,
        };

        if (this.dataModel.isWizardMode) {
          bookingDataToSave.staff_id = this.booking.staff_id;
          bookingDataToSave.branch_id = this.booking.branch_id;
          bookingDataToSave.service_id = this.selectedServiceIdsAsArray;
        }

        let title = '';
        if (this.isPeriodicBooking) {
          bookingDataToSave.timing_config = this.periodicConfig;
          bookingDataToSave.last_booking_date = this.lastBookingDateMode === 'specified' ? this.lastBookingDate : null;

          if (this.isEditing) {
            bookingDataToSave.scope = this.scope;
            if (this.scope !== 'all') {
              bookingDataToSave.target_date = this.targetDate;
            }
            await bookingApi.updatePeriodicBooking(this.periodicBookingIdFromRoute, bookingDataToSave);
            title = "儲存成功";
          } else {
            await bookingApi.createPeriodicBooking(this.bookingPresetIdFromRoute, bookingDataToSave);
            title = "新增成功";
          }
        } else {
          if (this.isEditing) {
            // 編輯表單
            await bookingApi.updateBooking(
              this.bookingPresetIdFromRoute,
              this.bookingIdFromRoute,
              bookingDataToSave
            );
            title = "儲存成功";
          } else {
            // 新增表單
            await bookingApi.createBooking(
              this.bookingPresetIdFromRoute,
              bookingDataToSave
            );
            title = "新增成功";
          }
        }

        const result = await this.$swal({
          title,
          type: "success",
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "確認",
          confirmButtonColor: "#41b882",
          cancelButtonText: "回上一頁",
          cancelButtonClass: "cancel-button",
          allowOutsideClick: false,  // 禁止點擊視窗外的區域
        });

        if (!result.value) {
          this.$router.go(-1);
        }

        return true;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
        return false;
      } finally {
        this.showLoading = false;
      }
    },
    async fetchBookingPreset() {
      if (!this.bookingPresetIdFromRoute) {
        this.$swal("錯誤", "無法取得讀取 ID", "error");
        return;
      }

      try {
        this.bookingPreset = _.get(await bookingApi.getBookingPreset(this.bookingPresetIdFromRoute), 'data.data', {});
        bookingBusinessLogic.setBookingPresetDataModel(this.dataModel)
        serviceDataFetcher.setServiceFieldMappings(this.dataModel.serviceFieldMappings)

        if (_.get(this.bookingPreset, 'config.fields', []).length === 0) {
          this.$swal("提醒", "此預約尚未設定欄位，須設定欄位後才能新增及編輯預約", "warning");
          this.cancel();
          return;
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料錯誤", "error");
        this.cancel();
      }
    },
    async fetchOneTimeBooking() {
      try {
        const response = await bookingApi.getBooking(
          this.bookingPresetIdFromRoute,
          this.bookingIdFromRoute
        );
        this.oneTimeBooking = response.data;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取錯誤", "error");
      }
    },
    setBookingMainData(bookingFormat, dynamicFormData) {
      this.booking = bookingFormat;

      this.start_at = this.booking.start_at;
      this.end_at = moment(this.booking.end_at).add(1, "second").format("YYYY-MM-DD HH:mm:ss");
      this.selectedCustomer = this.booking.customer;

      if (this.booking.service_id) {
        if (this.canMultipleSelect) {
          this.booking.service_id = JSON.parse(this.booking.service_id);
        } else {
          try {
            this.booking.service_id =
              JSON.parse(this.booking.service_id)[0] ?? null;
          } catch (e) {
            // console.error(e);
          }
        }
      } else {
        this.booking.service_id = [];
      }

      this.inputForm = DynamicFormField.fromArray(this.bookingPreset.config.fields).reduce((acc, fieldModel) => {
        let fieldValue = null;

        if (Array.isArray(dynamicFormData)) {
          // Regular booking stores data in 'data' array
          fieldValue = dynamicFormData.find(d => d.field_id === fieldModel._id)?.value ?? null;
        } else if (dynamicFormData && typeof dynamicFormData === 'object') {
          // Periodic booking stores data in 'field_values' object
          fieldValue = dynamicFormData[fieldModel._id] ?? null;
        }

        if (fieldValue !== null) {
          if (this.dataModel?.startAtFieldId === fieldModel._id) {
            fieldValue = this.booking.start_at;
          } else if (this.dataModel?.endAtFieldId === fieldModel._id) {
            fieldValue = this.booking.end_at;
          }
        }

        return { ...acc, [fieldModel._id]: fieldModel.prepareFieldValueForTwoWayBinding(fieldValue) };
      }, {});
    },
    async fetchPeriodicBooking() {
      try {
        this.periodicBooking = _.get(await bookingApi.getPeriodicBooking(this.periodicBookingIdFromRoute), 'data.data', null);
      } catch (error) {
        console.error('Failed to fetch periodic booking:', error);
        this.$bvToast.toast('無法載入週期性預約資料', {
          title: '錯誤',
          variant: 'danger'
        });
        return;
      }

      this.lastBookingDate = this.periodicBooking.last_booking_date;
      this.lastBookingDateMode = this.periodicBooking.last_booking_date ? 'specified' : null;
      this.periodicConfig = { ...this.periodicBooking.timing_config };

      if (!this.bookingIdFromRoute) {
        // Calculate start_at and end_at for the specific occurrence date
        const originalStartAt = moment(this.periodicBooking.start_at);
        const originalEndAt = moment(this.periodicBooking.end_at);
        const occurrenceDate = moment(this.targetDate);

        // Set the occurrence date with the source time
        this.periodicBooking.start_at = occurrenceDate
          .clone()
          .hour(originalStartAt.hour())
          .minute(originalStartAt.minute())
          .second(originalStartAt.second())
          .format('YYYY-MM-DD HH:mm:ss');

        this.periodicBooking.end_at = occurrenceDate
          .clone()
          .hour(originalEndAt.hour())
          .minute(originalEndAt.minute())
          .second(originalEndAt.second())
          .format('YYYY-MM-DD HH:mm:ss');
      }

      this.originalStartAt = this.periodicBooking.start_at;
    },
    async handleStaffSelected(staff) {
      this.booking.staff_id = staff.id;
      this.booking.staff = staff;

      if (bookingBusinessLogic.servicesAreBoundToStaffs()) {
        this.booking.service_id = this.canMultipleSelect ? [] : null
        await serviceDataFetcher.fetchServicesByStaff(this.collectionIdForService, this.booking.staff_id)
      }
    },
    async handleBranchChange() {
      this.booking.branch_id = this.selectedBranchId
      this.$set(this.inputForm, this.branchFieldId, this.branchValueForDynamicForm)

      if (bookingBusinessLogic.servicesAreBoundToBranches()) {
        this.booking.service_id = this.canMultipleSelect ? [] : null
        await serviceDataFetcher.fetchServicesByBranch(this.collectionIdForService, this.booking.branch_id)
      }

      if (bookingBusinessLogic.staffsAreBoundToBranches()) {
        await staffDataFetcher.fetchAvailableStaffs(null, this.booking.branch_id, this.bookingPresetIdFromRoute)
      }
    },
    async handleServiceSelected() {
      this.$set(this.inputForm, this.serviceFieldId, this.serviceValueForDynamicForm)

      if (bookingBusinessLogic.staffsAreBoundToServices()) {
        await staffDataFetcher.fetchAvailableStaffs(this.selectedServiceIdsAsArray, this.booking.branch_id, this.bookingPresetIdFromRoute)
      }
    },
    handleRecurrenceTypeChange() {
      this.periodicConfig.period_day_of_week = null;
      this.periodicConfig.period_day_of_month = null;
    },
    async handleSelectCustomer(customer) {
      this.$swal({
        title: "確定要加入會員嗎",
        html: `
          <div class="d-block">
            <div class="my-3 text-left">
              <div>會員姓名:${customer.name}</div>
            </div>
          </div>`,
        type: "warning",
        showConfirmButton: true,
        showCancelButton: true,
        cancelButtonText: "返回",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "確認加入",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.value) {
          try {
            this.selectedCustomer = customer;
          } catch (e) {
            console.error(e);
          } finally {
            this.$refs.staffAddCustomer.refreshSearch();
            this.$refs.staffAddCustomer.closeModal();
          }
        }
      });
    },
    displayCustomerInfo(customer) {
      return (
        (customer.outer_code ? customer.outer_code + " " : "") +
        (customer.mobile_phone ?? "")
      );
    },
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
.sorted-fields {
  display: flex;
  flex-direction: column;
}

.mutation-scope {
  background-color: #f4f4f4;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all .3s;

  & + & {
    margin-left: 8px;
  }

  &:hover {
    background-color: #e8e8e8;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;

    * {
      pointer-events: none;
    }
  }

  * {
    cursor: pointer;
  }

  &__note {
    margin: 0;
    font-size: 12px;
    color: #666;
  }
}
</style>

<style>
.cancel-button {
  background-color: #ffffff !important;
  color: #41b882 !important; /* 文字顏色，確保可讀性 */
  border: 2px solid #41b882 !important; /* 正確設定邊框 */
}
</style>
